import type { IAttachment, IEntityLogEntry, IEscalation } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const base = (projectId: string) => `/api/v1/projects/${projectId}/escalations`;

export interface CreateEscalationInput {
  title: string;
  description?: string | null;
  severity: string;
  categoryId?: string | null;
  escalatedToId: string;
  ownerId: string;
  dateRaised?: string;
  /** `null` explicitly clears the target resolution date on an existing escalation; `undefined` leaves it unset at creation. */
  targetResolutionDate?: string | null;
  resolutionActionTaken?: string | null;
  linkedIssueIds?: string[];
  linkedRiskIds?: string[];
  linkedDependencyIds?: string[];
}
export type UpdateEscalationInput = Partial<CreateEscalationInput>;

export async function fetchEscalations(projectId: string): Promise<IEscalation[]> {
  const res = await apiFetch(base(projectId));
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch escalations'));
  return res.json() as Promise<IEscalation[]>;
}

export async function createEscalation(projectId: string, data: CreateEscalationInput): Promise<IEscalation> {
  const res = await apiFetch(base(projectId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create escalation'));
  return res.json() as Promise<IEscalation>;
}

export async function updateEscalation(projectId: string, escalationId: string, data: UpdateEscalationInput): Promise<IEscalation> {
  const res = await apiFetch(`${base(projectId)}/${escalationId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update escalation'));
  return res.json() as Promise<IEscalation>;
}

export async function transitionEscalation(projectId: string, escalationId: string, toStep: string): Promise<IEscalation> {
  const res = await apiFetch(`${base(projectId)}/${escalationId}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ toStep }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to change escalation status'));
  return res.json() as Promise<IEscalation>;
}

export async function setEscalationActive(projectId: string, escalationId: string, active: boolean): Promise<IEscalation> {
  const res = await apiFetch(`${base(projectId)}/${escalationId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update escalation status'));
  return res.json() as Promise<IEscalation>;
}

export async function fetchEscalationLog(projectId: string, escalationId: string): Promise<IEntityLogEntry[]> {
  const res = await apiFetch(`${base(projectId)}/${escalationId}/log`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch escalation log'));
  return res.json() as Promise<IEntityLogEntry[]>;
}

export async function addEscalationLogEntry(projectId: string, escalationId: string, note: string): Promise<IEntityLogEntry[]> {
  const res = await apiFetch(`${base(projectId)}/${escalationId}/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add log entry'));
  return res.json() as Promise<IEntityLogEntry[]>;
}

export async function fetchEscalationAttachments(projectId: string, escalationId: string): Promise<IAttachment[]> {
  const res = await apiFetch(`${base(projectId)}/${escalationId}/attachments`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch attachments'));
  return res.json() as Promise<IAttachment[]>;
}

export async function uploadEscalationAttachment(projectId: string, escalationId: string, file: File): Promise<IAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiFetch(`${base(projectId)}/${escalationId}/attachments`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to upload attachment'));
  return res.json() as Promise<IAttachment>;
}

export async function removeEscalationAttachment(projectId: string, escalationId: string, attachmentId: string): Promise<void> {
  const res = await apiFetch(`${base(projectId)}/${escalationId}/attachments/${attachmentId}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to remove attachment'));
}

export function escalationAttachmentDownloadUrl(projectId: string, escalationId: string, attachmentId: string): string {
  return `${base(projectId)}/${escalationId}/attachments/${attachmentId}/download`;
}
